// Localização: frontend/src/components/Prontuario/Modals/UploadDocumentoModal.jsx (versão 0.1.0)

import React, { useState } from 'react';
import { Upload, X, FileText, ChevronDown } from 'lucide-react';
import Modal from '../../UI/Modal';
import PrimaryButton from '../../Common/PrimaryButton';
import DropdownMenu from '../../UI/DropdownMenu';

const CATEGORIAS = [
    { value: '', label: 'Selecione uma categoria' },
    { value: 'exame', label: 'Exames' },
    { value: 'receita', label: 'Receitas' },
    { value: 'atestado', label: 'Atestados' },
    { value: 'historico-medico', label: 'Histórico Médico' },
    { value: 'consentimento', label: 'Consentimentos' },
    { value: 'identidade', label: 'Identidade' },
    { value: 'outro', label: 'Outros' }
];

// Ícones para cada categoria (opcional, pode ser removido se não houver ícones apropriados)
const CATEGORIA_ICONS = {
    'exame': FileText,
    'receita': FileText,
    'atestado': FileText,
    'historico-medico': FileText,
    'consentimento': FileText,
    'identidade': FileText,
    'outro': FileText
};

const MAX_FILE_SIZE = 10 * 1024 * 1024; // 10MB

const UploadDocumentoModal = ({ isOpen, onClose, onUpload }) => {
    const [arquivo, setArquivo] = useState(null);
    const [titulo, setTitulo] = useState('');
    const [descricao, setDescricao] = useState('');
    const [categoria, setCategoria] = useState('');
    const [isUploading, setIsUploading] = useState(false);
    const [erro, setErro] = useState('');

    const formatFileSize = (bytes) => {
        if (!bytes) return '0 B';
        const mb = bytes / (1024 * 1024);
        return `${mb.toFixed(2)} MB`;
    };

    const handleFileChange = (e) => {
        const file = e.target.files[0];
        setErro('');

        if (!file) {
            setArquivo(null);
            return;
        }

        // Validar tipo
        if (file.type !== 'application/pdf') {
            setErro('Apenas arquivos PDF são permitidos.');
            setArquivo(null);
            return;
        }

        // Validar tamanho
        if (file.size > MAX_FILE_SIZE) {
            setErro(`Arquivo muito grande. Tamanho máximo: ${formatFileSize(MAX_FILE_SIZE)}`);
            setArquivo(null);
            return;
        }

        setArquivo(file);
        // Auto-preencher título se vazio
        if (!titulo) {
            setTitulo(file.name.replace('.pdf', ''));
        }
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setErro('');

        // Validações
        if (!arquivo) {
            setErro('Por favor, selecione um arquivo PDF.');
            return;
        }

        if (!titulo.trim()) {
            setErro('O título é obrigatório.');
            return;
        }

        if (titulo.trim().length < 3) {
            setErro('O título deve ter pelo menos 3 caracteres.');
            return;
        }

        if (titulo.trim().length > 255) {
            setErro('O título não pode ter mais de 255 caracteres.');
            return;
        }

        if (descricao.length > 1000) {
            setErro('A descrição não pode ter mais de 1000 caracteres.');
            return;
        }

        setIsUploading(true);
        try {
            const formData = new FormData();
            formData.append('documento', arquivo);
            formData.append('titulo', titulo.trim());
            if (descricao.trim()) {
                formData.append('descricao', descricao.trim());
            }
            if (categoria) {
                formData.append('categoria', categoria);
            }

            await onUpload(formData);
            
            // Reset form
            setArquivo(null);
            setTitulo('');
            setDescricao('');
            setCategoria('');
            setErro('');
        } catch (error) {
            setErro(error?.response?.data?.message || 'Erro ao fazer upload do documento.');
        } finally {
            setIsUploading(false);
        }
    };

    const handleClose = () => {
        if (!isUploading) {
            setArquivo(null);
            setTitulo('');
            setDescricao('');
            setCategoria('');
            setErro('');
            onClose();
        }
    };

    return (
        <Modal isOpen={isOpen} onClose={handleClose} title="Upload de Documento" size="lg">
            <form onSubmit={handleSubmit} className="space-y-4">
                {/* Campo de arquivo */}
                <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                        Arquivo PDF <span className="text-red-500">*</span>
                    </label>
                    <div className="relative">
                        <input
                            type="file"
                            accept=".pdf"
                            onChange={handleFileChange}
                            disabled={isUploading}
                            className="hidden"
                            id="file-upload"
                        />
                        <label
                            htmlFor="file-upload"
                            className={`flex flex-col items-center justify-center w-full h-32 border-2 border-dashed rounded-lg cursor-pointer transition-colors ${
                                arquivo
                                    ? 'border-emerald-500 bg-emerald-50'
                                    : 'border-gray-300 hover:border-emerald-500 hover:bg-gray-50'
                            } ${isUploading ? 'opacity-50 cursor-not-allowed' : ''}`}
                        >
                            {arquivo ? (
                                <div className="flex flex-col items-center">
                                    <FileText className="w-8 h-8 text-emerald-600 mb-2" />
                                    <p className="text-sm font-medium text-emerald-900">{arquivo.name}</p>
                                    <p className="text-xs text-gray-500 mt-1">{formatFileSize(arquivo.size)}</p>
                                </div>
                            ) : (
                                <div className="flex flex-col items-center">
                                    <Upload className="w-8 h-8 text-gray-400 mb-2" />
                                    <p className="text-sm text-gray-600">
                                        <span className="font-semibold text-emerald-600">Clique para selecionar</span> ou arraste o arquivo
                                    </p>
                                    <p className="text-xs text-gray-500 mt-1">PDF até {formatFileSize(MAX_FILE_SIZE)}</p>
                                </div>
                            )}
                        </label>
                    </div>
                    {arquivo && (
                        <button
                            type="button"
                            onClick={() => setArquivo(null)}
                            disabled={isUploading}
                            className="mt-2 text-sm text-red-600 hover:text-red-700 flex items-center gap-1"
                        >
                            <X className="w-4 h-4" />
                            Remover arquivo
                        </button>
                    )}
                </div>

                {/* Título */}
                <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                        Título <span className="text-red-500">*</span>
                    </label>
                    <input
                        type="text"
                        value={titulo}
                        onChange={(e) => {
                            const value = e.target.value;
                            if (value.length <= 255) {
                                setTitulo(value);
                                setErro('');
                            }
                        }}
                        disabled={isUploading}
                        placeholder="Ex: Exame de Sangue - Janeiro 2024"
                        className={`w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 ${
                            erro && !titulo.trim() 
                                ? 'border-red-300 focus:ring-red-500' 
                                : 'border-gray-300 focus:ring-emerald-500'
                        }`}
                        maxLength={255}
                        required
                    />
                    <p className="mt-1 text-xs text-gray-500">
                        {titulo.length}/255 caracteres
                    </p>
                </div>

                {/* Descrição */}
                <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                        Descrição (opcional)
                    </label>
                    <textarea
                        value={descricao}
                        onChange={(e) => {
                            const value = e.target.value;
                            if (value.length <= 1000) {
                                setDescricao(value);
                            }
                        }}
                        disabled={isUploading}
                        placeholder="Descrição adicional do documento..."
                        rows={3}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-emerald-500 resize-none"
                        maxLength={1000}
                    />
                    <p className="mt-1 text-xs text-gray-500">
                        {descricao.length}/1000 caracteres
                    </p>
                </div>

                {/* Categoria */}
                <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                        Categoria (opcional)
                    </label>
                    <DropdownMenu
                        trigger={
                            <button
                                type="button"
                                disabled={isUploading}
                                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-emerald-500 bg-white text-left flex items-center justify-between disabled:opacity-50 disabled:cursor-not-allowed"
                            >
                                <span className={categoria ? 'text-gray-900' : 'text-gray-500'}>
                                    {categoria 
                                        ? CATEGORIAS.find(cat => cat.value === categoria)?.label 
                                        : 'Selecione uma categoria'}
                                </span>
                                <ChevronDown className="w-4 h-4 text-gray-400" />
                            </button>
                        }
                        items={CATEGORIAS.filter(cat => cat.value !== '').map(cat => ({
                            id: cat.value,
                            label: cat.label,
                            icon: CATEGORIA_ICONS[cat.value] || FileText,
                            iconColor: 'text-gray-600',
                            onClick: () => setCategoria(cat.value),
                            className: categoria === cat.value ? 'bg-emerald-50' : ''
                        }))}
                        position="bottom-left"
                    />
                    {categoria && (
                        <button
                            type="button"
                            onClick={() => setCategoria('')}
                            disabled={isUploading}
                            className="mt-2 text-xs text-red-600 hover:text-red-700 flex items-center gap-1"
                        >
                            <X className="w-3 h-3" />
                            Remover categoria
                        </button>
                    )}
                </div>

                {/* Mensagem de erro */}
                {erro && (
                    <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg text-sm">
                        {erro}
                    </div>
                )}

                {/* Botões */}
                <div className="flex justify-end gap-3 pt-4 border-t">
                    <PrimaryButton
                        type="button"
                        variant="secondary"
                        onClick={handleClose}
                        disabled={isUploading}
                    >
                        Cancelar
                    </PrimaryButton>
                    <PrimaryButton
                        type="submit"
                        disabled={isUploading || !arquivo || !titulo.trim()}
                    >
                        {isUploading ? 'Enviando...' : 'Enviar Documento'}
                    </PrimaryButton>
                </div>
            </form>
        </Modal>
    );
};

export default UploadDocumentoModal;

